import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Upload, CreditCard, ExternalLink } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [businessName, setBusinessName] = useState(user?.business_name || "");
  const [logo, setLogo] = useState(user?.logo_data_url || "");
  const [link, setLink] = useState(user?.google_review_link || "");
  const [replyTo, setReplyTo] = useState(user?.reply_to_email || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setBusinessName(user.business_name || "");
      setLogo(user.logo_data_url || "");
      setLink(user.google_review_link || "");
      setReplyTo(user.reply_to_email || user.email || "");
    }
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/business", {
        business_name: businessName,
        logo_data_url: logo,
        google_review_link: link,
        reply_to_email: replyTo,
      });
      toast.success("Saved");
      await refresh();
    } catch (e) { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const onLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2MB"); return; }
    const r = new FileReader();
    r.onload = () => setLogo(r.result);
    r.readAsDataURL(f);
  };

  const startCheckout = async () => {
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Could not start checkout"); }
  };
  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch { toast.error("No billing account yet — subscribe first."); }
  };

  const state = user?.access?.state;
  const sub = user?.subscription_status;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="font-display font-bold text-3xl md:text-4xl text-slate-900">Settings</h1>

      <Card className="rounded-2xl p-6">
        <h2 className="font-display font-bold text-lg mb-4">Business profile</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Business name</label>
            <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="h-12 rounded-lg" data-testid="settings-business-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Reply-to email</label>
            <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} type="email" className="h-12 rounded-lg" data-testid="settings-reply-to" />
            <p className="text-xs text-slate-500 mt-1">Customers replying to review emails will land here.</p>
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Google review link</label>
            <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://search.google.com/local/writereview?placeid=..." className="h-12 rounded-lg" data-testid="settings-review-link" />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Logo</label>
            <div className="flex items-center gap-4">
              <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 hover:bg-slate-50 text-sm font-medium">
                <Upload className="h-4 w-4" /> Change logo
                <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="settings-logo-input" />
              </label>
              {logo && <img src={logo} alt="logo" className="h-12 rounded border border-slate-200" />}
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90" data-testid="settings-save">Save changes</Button>
        </div>
      </Card>

      <Card className="rounded-2xl p-6">
        <h2 className="font-display font-bold text-lg mb-4">Billing</h2>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-sm text-slate-500">Status:</span>
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${sub === "active" ? "bg-emerald-50 text-emerald-700" : sub === "past_due" ? "bg-amber-50 text-amber-800" : sub === "canceled" ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"}`} data-testid="settings-sub-status">
            {sub === "trialing" ? `Trial · ${user?.access?.trial_days_left ?? 0} days left` : (sub || "trialing")}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {sub !== "active" && (
            <Button onClick={startCheckout} className="rounded-full bg-primary hover:bg-primary/90" data-testid="settings-subscribe">
              <CreditCard className="h-4 w-4 mr-2" /> Subscribe — $29/month
            </Button>
          )}
          <Button onClick={openPortal} variant="outline" className="rounded-full" data-testid="settings-portal">
            <ExternalLink className="h-4 w-4 mr-2" /> Manage billing (Stripe portal)
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-4">Cancel anytime from the billing portal. No contracts.</p>
      </Card>

      <Card className="rounded-2xl p-6">
        <h2 className="font-display font-bold text-lg mb-4">Account</h2>
        <div className="text-sm text-slate-600">Email: <span className="font-medium text-slate-900">{user?.email}</span></div>
        <p className="text-xs text-slate-500 mt-3">To change your password, log out and use "Forgot password".</p>
      </Card>
    </div>
  );
}
